import React from "react";

import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";

import { Button, Icon, IconBox } from "@proda-ai/ui-kit";

import type { ExtractionsErrorListProps } from "./ExtractionsErrorList";
import ExtractionsErrorList from "./ExtractionsErrorList";
import { SIDEBAR_WIDTH } from "@/ui/layouts/LayoutFullScreenWithCollapsibleSidebar";
import Typography from "@/ui/atoms/Typography";
import Badge from "@/ui/atoms/Badge";
import ExtractionLabelset from "./ExtractionLabelset/ExtractionLabelset";
import ExtractionTableSelector from "./ExtractionTableSelector/ExtractionTableSelector";
import type { TLabelsetInfo, TTableLocation } from "@/apis/odin/apiODIN";

export type ExtractionsSidebarProps = BoxProps & {
    fileName: string;
    allFoundTables: Array<TTableLocation> | null;
    selectedTable: TTableLocation | null;
    onTableChange: (table: TTableLocation) => void;
    description: string;
    showErrors: boolean;
    showLabelset: boolean;
    selectedColumn: SelectedColumn | null;
    labelsetExtractionInfo: Array<TLabelsetInfo> | null;
    labelsetSourceId: number | null;
    ExtractionsErrorListProps: ExtractionsErrorListProps;
    onLabelsetSourceChange: (id: number | null) => void;
    canChangeTable?: boolean;
    onLlmReExtract?: () => void;
    llmReExtractStatus?: "idle" | "pending" | "resolved" | "rejected";
    isFromLlm: boolean;
};

export const testIds = {
    filename: "ExtractionsSidebar-filename",
    extractionLabelset: "ExtractionsSidebar-extractionLabelset",
    operatingStatementType: "ExtractionsSidebar-operatingStatementType",
    llmReExtractButton: "ExtractionsSidebar-llmReExtractButton",
};

export type SelectedColumn = {
    fileName: string;
    columnGroup: string;
    status: "active" | "inactive" | "partial";
    currentUpload: boolean;
};

const ExtractionsSidebar: React.FC<ExtractionsSidebarProps> = ({
    sx,
    fileName,
    allFoundTables,
    canChangeTable = true,
    selectedTable,
    onTableChange,
    description,
    showErrors,
    showLabelset,
    selectedColumn,
    labelsetExtractionInfo,
    labelsetSourceId,
    onLabelsetSourceChange,
    onLlmReExtract,
    llmReExtractStatus,
    isFromLlm,
    ExtractionsErrorListProps = {
        errors: {},
        onViewError: () => {},
        onDismissWarnings: () => {},
        onPrevious: () => {},
        onNext: () => {},
    },
    ...otherProps
}) => {
    const selectedColumnStatus = () => {
        if (selectedColumn?.status === "active") {
            return (
                <Box
                    sx={{
                        color: "var(--proda-green-120)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        fontFamily: "Noto Sans",
                        fontWeight: "700",
                        lineHeight: "15px",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "12px",
                            height: "12px",
                            borderRadius: "50%",
                            backgroundColor: "var(--proda-green-120)",
                            color: "white",
                            marginRight: "4px",
                            fontWeight: "bold",
                            flexShrink: 0,
                        }}
                    >
                        <IconBox>
                            <Icon icon="check" size="2xs" />
                        </IconBox>
                    </Box>
                    Active
                </Box>
            );
        } else if (selectedColumn?.status === "inactive") {
            return (
                <Box
                    sx={{
                        color: "var(--proda-black-10)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        fontFamily: "Noto Sans",
                        fontWeight: "700",
                        lineHeight: "15px",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "12px",
                            height: "12px",
                            borderRadius: "50%",
                            backgroundColor: "var(--proda-black-10)",
                            color: "white",
                            marginRight: "4px",
                            fontWeight: "bold",
                            flexShrink: 0,
                        }}
                    >
                        <IconBox>
                            <Icon icon="xmark" size="2xs" />
                        </IconBox>
                    </Box>
                    Inactive
                </Box>
            );
        } else if (selectedColumn?.status === "partial") {
            return (
                <Box
                    sx={{
                        color: "var(--proda-neon-blue)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        fontFamily: "Noto Sans",
                        fontWeight: "700",
                        lineHeight: "15px",
                        gap: "4px",
                    }}
                >
                    <IconBox>
                        <Icon
                            icon="list"
                            size="sm"
                            color="var(--proda-neon-blue)"
                        />
                    </IconBox>
                    Partially Active
                </Box>
            );
        }
        return null;
    };
    return (
        // this is to set the width of the sidebar to be the same as the width of the sidebar in the layout so that it does not change based on the transition
        <Box sx={{ p: 3, pt: 0, width: SIDEBAR_WIDTH, ...sx }} {...otherProps}>
            <Box sx={{ mb: 3 }}>
                <Box
                    sx={{
                        fontSize: "16px",
                        color: "var(--proda-black-80)",
                        fontFamily: "'DM Sans', sans-serif",
                    }}
                >
                    {description}
                </Box>
            </Box>
            <Box
                sx={{
                    height: "40px",
                    position: "relative",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "var(--proda-white-l95)",
                    padding: "16px",
                    borderRadius: "4px",
                    mb: "24px",
                }}
            >
                <IconBox
                    style={{
                        position: "absolute",
                        top: "-5px",
                        right: "-2px",
                        transform: "rotate(-135deg)",
                    }}
                >
                    <Icon
                        icon="caret-down"
                        size="lg"
                        color={"var(--proda-celtic-blue)"}
                    />
                </IconBox>
                <IconBox mr={1}>
                    <Icon icon="file-xls" color="var(--proda-black)" />
                </IconBox>
                <Box
                    sx={{
                        fontFamily: "Noto Sans",
                        fontSize: "12px",
                        fontWeight: 500,
                        lineHeight: "15px",
                        textAlign: "center",
                    }}
                    data-testid={testIds.filename}
                >
                    {fileName}
                </Box>
            </Box>
            {selectedColumn && (
                <>
                    <Typography variant="h3" sx={{ mb: "8px" }}>
                        Selected
                    </Typography>
                    <Box
                        sx={{
                            position: "relative",
                            boxSizing: "border-box",
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: "var(--proda-light-blue-l95)",
                            padding: "16px",
                            borderRadius: "4px",
                            flexDirection: "column",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                                mb: "16px",
                            }}
                        >
                            <Box
                                sx={{
                                    fontSize: "14px",
                                    fontFamily: "Noto Sans",
                                    fontWeight: "700",
                                    fontStyle: "normal",
                                    lineHeight: "19px",
                                }}
                            >
                                {selectedColumn.columnGroup}
                            </Box>
                            {selectedColumnStatus()}
                        </Box>
                        <Typography
                            variant="body3"
                            sx={{ fontWeight: "700", width: "100%" }}
                        >
                            Column Origin:
                        </Typography>
                        <Box
                            sx={{
                                height: "40px",
                                width: "100%",
                                position: "relative",
                                boxSizing: "border-box",
                                display: "flex",
                                alignItems: "center",
                                backgroundColor: "var(--proda-white)",
                                padding: "16px 8px",
                                borderRadius: "4px",
                                my: "8px",
                            }}
                        >
                            <IconBox mr={1}>
                                <Icon
                                    icon="file-xls"
                                    color="var(--proda-black-10)"
                                />
                            </IconBox>
                            <Box
                                sx={{
                                    fontFamily: "Noto Sans",
                                    fontSize: "12px",
                                    fontWeight: 500,
                                    lineHeight: "15px",
                                    textAlign: "center",
                                }}
                            >
                                {selectedColumn.fileName}
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-start",
                                width: "100%",
                            }}
                        >
                            {selectedColumn.currentUpload ? (
                                <Badge
                                    backgroundColor="proda-celtic-blue"
                                    borderColor="proda-celtic-blue"
                                    leadingIcon={null}
                                >
                                    <Typography
                                        variant="body3"
                                        sx={{ color: "var(--proda-white)" }}
                                    >
                                        Current Upload
                                    </Typography>
                                </Badge>
                            ) : (
                                <Badge
                                    backgroundColor="proda-white-130"
                                    borderColor="proda-white-130"
                                    leadingIcon={null}
                                >
                                    Existing Upload
                                </Badge>
                            )}
                        </Box>
                    </Box>
                </>
            )}
            {allFoundTables && canChangeTable && (
                <Box
                    sx={{
                        marginTop: "24px",
                    }}
                >
                    <ExtractionTableSelector
                        allFoundTables={allFoundTables}
                        selectedTable={selectedTable}
                        onTableChange={onTableChange}
                        isPdf={fileName.toLowerCase().endsWith(".pdf")}
                    />
                </Box>
            )}
            {showLabelset && (
                <Box
                    data-testid={testIds.extractionLabelset}
                    sx={{
                        marginTop: "24px",
                    }}
                >
                    <ExtractionLabelset
                        labelsetExtractionInfo={labelsetExtractionInfo || []}
                        labelsetSourceId={labelsetSourceId}
                        onLabelsetSourceChange={onLabelsetSourceChange}
                    />
                </Box>
            )}
            {onLlmReExtract && (
                <Box
                    sx={{
                        marginTop: "24px",
                        padding: "16px",
                        backgroundColor: "var(--proda-white-l95)",
                        borderRadius: "8px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                    }}
                >
                    {isFromLlm || llmReExtractStatus === "resolved" ? (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                            }}
                        >
                            <Icon
                                icon="sparkles"
                                size="sm"
                                color="var(--proda-black-10)"
                            />
                            <Typography
                                variant="body3"
                                sx={{
                                    color: "var(--proda-black-10)",
                                    fontStyle: "italic",
                                }}
                            >
                                AI can make mistakes, check important data.
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Typography
                                variant="body3"
                                sx={{
                                    color: "var(--proda-black-10)",
                                }}
                            >
                                Having trouble with extraction results? Try
                                re-extracting using AI.
                            </Typography>
                            <Button
                                data-testid={testIds.llmReExtractButton}
                                pattern="secondary"
                                size="small"
                                onClick={onLlmReExtract}
                                disabled={llmReExtractStatus === "pending"}
                                style={{ width: "100%" }}
                            >
                                {llmReExtractStatus === "pending"
                                    ? "Extracting..."
                                    : "Extract with AI"}
                            </Button>
                        </>
                    )}
                </Box>
            )}
            {showErrors && (
                <Box
                    sx={{
                        marginTop: "24px",
                    }}
                >
                    <ExtractionsErrorList {...ExtractionsErrorListProps} />
                </Box>
            )}
        </Box>
    );
};

export default ExtractionsSidebar;
